import EventEmitter from "eventemitter3";
import $ from "jquery";

export class UserInputSliderSelector extends EventEmitter {

  static SOUND_MIN_INTERVAL_MS = 80; // ~12 sounds/sec max
  static COMMIT_DEBOUNCE_MS = 40;   // debounce change events emitting

  constructor(target, snapPoints = null) {
    super();

    this.$widget = target;
    this.snapPoints = Array.isArray(snapPoints) && snapPoints.length > 0 ? snapPoints : null;
    this.$sliderTrack = this.$widget.find(".user-input-slider");
    this.$valueLabel = this.$widget.find(".user-input-current .user-input-value");
    this.$unitLabel = this.$widget.find(".user-input-unit");
    this.$dragHandle = this.$widget.find(".user-input-control");
    this.$warnings = this.$widget.find(".user-input-warnings");
    this.$warningMin = this.$widget.find(".user-input-minimum-reached");
    this.$warningMax = this.$widget.find(".user-input-maximum-reached");

    // Check if a temperature unit conversion is needed
    this.unitConversion = this.getUnitConversion();

    // Get CSS variable values
    this.minValue = parseInt(this.$sliderTrack.css("--user-input-min"));
    this.maxValue = parseInt(this.$sliderTrack.css("--user-input-max"));
    this.currentValue = parseInt(this.$sliderTrack.css("--user-input-current"));
    this.initialValue = parseInt(this.$sliderTrack.css("--user-input-initial"));

    // Drag state
    this.isDragging = false;
    this.dragPositionRatio = 0; // (left = 0, right = 1)

    // Avoid calling init two times when UserInputSliderSelector is extended
    if (this.constructor === UserInputSliderSelector) {
      this.init();
    }
  }

  getUnitConversion() {
    const conversion = app.getTemperatureUnitConversion();
    if (!conversion) return null;

    const currentUnit = this.$unitLabel.first().text().trim();
    if (currentUnit !== conversion.from) return null;

    return conversion;
  }

  init() {
    // Apply unit conversion to displayed labels if needed
    if (this.unitConversion) {
      this.$unitLabel.text(this.unitConversion.unit);

      this.$sliderTrack.find(".user-input-min .user-input-text, .user-input-half .user-input-text, .user-input-max .user-input-text").each((i, el) => {
        let val = parseFloat($(el).text());

        if (!isNaN(val)) {
          $(el).text(Math.round(this.unitConversion.convert(val)));
        }
      });

      this.updateVisually(this.currentValue);
    }

    // Unique namespace per instance to avoid multiple selectors interfering
    this.eventNamespace = `UserInputSliderSelector_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Start dragging when the user presses the track — do NOT update position yet
    this.$sliderTrack.off("mousedown touchstart").on("mousedown touchstart", (e) => {
      // User is not actually dragging, clicking on an option
      if ($(e.target).closest("ul.options li").length > 0) return;

      e.preventDefault();
      e.stopPropagation();

      this.isDragging = true;
      this.$sliderTrack.addClass("dragging");
    });

    // While dragging, update based on horizontal pointer position
    $(document).off(`mousemove.${this.eventNamespace} touchmove.${this.eventNamespace}`)
      .on(`mousemove.${this.eventNamespace} touchmove.${this.eventNamespace}`, (e) => {
        if (!this.isDragging) return;

        // Get touch position and calculate the new position
        const touchPoint = e.originalEvent.touches ? e.originalEvent.touches[0] : null;
        const pointerX = touchPoint ? touchPoint.pageX : e.pageX;
        this.updateFromPointer(pointerX);
      });

    // On release, commit the new value
    $(document).off(`mouseup.${this.eventNamespace} touchend.${this.eventNamespace}`)
      .on(`mouseup.${this.eventNamespace} touchend.${this.eventNamespace}`, (e) => {
        if (!this.isDragging) return;

        this.isDragging = false;
        this.$sliderTrack.removeClass("dragging");

        // Get last touch position and calculate the new position
        const touchPoint = e.originalEvent.changedTouches ? e.originalEvent.changedTouches[0] : null;
        const pointerX = touchPoint ? touchPoint.pageX : e.pageX;
        this.updateFromPointer(pointerX, true);
      });
  }

  /**
   * Convert horizontal pointer position to a normalized ratio (0–1)
   * CSS handles the actual rendering via --user-input-current / --progress
   */
  updateFromPointer(pointerX, snap = false) {
    const sliderLeftEdge = this.$sliderTrack.offset().left;
    const sliderWidth = this.$sliderTrack.outerWidth();

    // Pointer position inside the slider
    let distanceFromLeft = pointerX - sliderLeftEdge;

    // Clamp inside slider bounds
    distanceFromLeft = Math.max(0, Math.min(sliderWidth, distanceFromLeft));

    // Convert position to ratio (left = 0, right = 1)
    this.dragPositionRatio = distanceFromLeft / sliderWidth;

    let newValue = parseInt(window.map(this.dragPositionRatio, 0, 1, this.minValue, this.maxValue).toFixed(0));
    if (snap) newValue = this.snapValue(newValue);
    this.updateVisually(newValue);
    this.valueChange(newValue);
  }

  /**
   * Update visual
   */
  updateVisually(value) {
    // Update position (always uses original scale)
    this.$sliderTrack.css("--user-input-current", value);

    // Display converted value if a unit conversion is set
    let displayValue = value;
    if (this.unitConversion) {
      displayValue = Math.round(this.unitConversion.convert(value));
    }
    this.$valueLabel.text(displayValue);

    // Handle visual warnings
    const atMin = value <= this.minValue;
    const atMax = value >= this.maxValue;
    this.$warnings.toggleClass("active", atMin || atMax);
    this.$warningMin.toggleClass("active", atMin);
    this.$warningMax.toggleClass("active", atMax);
  }

  snapValue(value) {
    if (!this.snapPoints || this.snapPoints.length === 0) return value;
    return this.snapPoints.reduce((closest, point) =>
      Math.abs(point - value) < Math.abs(closest - value) ? point : closest
    );
  }

  valueChange(value) {
    if (!value || value === this.currentValue) return;

    // Throttle slider sound so fast dragging isn't overwhelming
    const now = performance.now();
    if (!this.lastSoundTime || now - this.lastSoundTime >= UserInputSliderSelector.SOUND_MIN_INTERVAL_MS) {
      const phaseChange = 0.9 + Math.random() * 0.6;
      // const phaseChange = 1
      app.audio.play("audio/SliderAi_1.wav", 1, true, null, false, phaseChange);
      this.lastSoundTime = now;
    }

    this.currentValue = value;
    this.change();
  }

  // Debounce change event emit to prevent super rapid changes that would stutter the app.
  // Pass immediate=true to skip the wait.
  change(immediate = false) {
    if (this._commitTimer) {
      clearTimeout(this._commitTimer);
      this._commitTimer = null;
    }

    if (immediate) {
      this.emitValueChanged();
      return;
    }

    this._commitTimer = setTimeout(() => {
      this._commitTimer = null;
      this.emitValueChanged();
    }, UserInputSliderSelector.COMMIT_DEBOUNCE_MS);
  }

  // Overridden by subclasses that emit a different value (e.g. the real value).
  emitValueChanged() {
    this.emit("userInputValueChanged", this.currentValue);
  }

  /**
   * Reset to initial state before showing
   */
  reset() {
    this.currentValue = this.snapValue(this.initialValue);
    this.updateVisually(this.currentValue);
    this.change(true); // reset is programmatic — commit now, don't wait for the debounce
  }
}
